/**
 * THE SURFACE REGISTRY (PX-W6.6, TASK-188E6E40, spec §6.1/§6.2).
 *
 * One typed array classifying EVERY entry of `ROUTE_PATHS` as live or concept.
 * It is the single source of truth for four things that used to drift apart:
 * the demo-boundary allow list, the triad audit's route set, the rail's concept
 * flags, and the README's live/concept tables.
 *
 * ⚠ It is DATA, not policy: nothing here decides access. It lives under
 * `src/surfaces/` (re-homed from `src/demo/` by TASK-33343000, spec §6.1, so
 * that `nav.ts` — which may not import `@/demo/*` — can derive rail state
 * from it) and is read by the audit suites, the rail model and the README
 * generator. `canAccess` remains the single enforcement point.
 *
 * ── The three shapes a route can have ──────────────────────────────────────
 *
 *  - `mode: 'live'` — the page renders live services, product furniture and
 *    session context ONLY. It must import nothing from `@/demo/*`; the boundary
 *    suite fails naming the file if it ever does.
 *  - `mode: 'concept'`, `conceptScope: 'page'` — the whole surface is demo
 *    content behind a `ConceptBadge`, and it is NETWORK-QUIET. This is spec
 *    §6.2's census (its mandatory floor) plus the extras the classification
 *    rule caught.
 *  - `mode: 'concept'`, `conceptScope: 'section'` — a HYBRID: a LIVE data spine
 *    with a badged concept section beside it (W6.3's project overview over the
 *    fixture agent panel, W6.4's utilisation over the fixture office table, and
 *    so on). W0 of this ticket found seven of these and recorded the shape as a
 *    deviation from the spec's two-way split: they legitimately import demo
 *    content, so the zero-network claim (AC-1.6) binds `conceptScope: 'page'`
 *    only — asserting it over a page that is SUPPOSED to fetch would have made
 *    the AC unsatisfiable on correct code. They stay `triad: 'data'`, so their
 *    live spine is still audited by AC-2.
 */

export type DemoMode = 'live' | 'concept'

/** How much of a concept route is demo content — see the module doc comment. */
export type ConceptScope = 'page' | 'section'

export interface DemoRouteEntry {
  /** Must be a member of `ROUTE_PATHS` (test-enforced, both directions). */
  path: string
  mode: DemoMode
  /** Human label for the README + audit table. */
  label: string
  /** Whether the view is data-backed and must implement the triad. */
  triad: 'data' | 'none'
  /**
   * The page module, relative to `src/`. Absent for redirect-only paths, which
   * render nothing of their own.
   */
  pageModule?: string
  /** Required on every concept entry. */
  conceptScope?: ConceptScope
  /** For concept entries: the wave/backend whose arrival retires the badge. */
  retireWhen?: string
}

/**
 * The registry row a concrete path lands on. `:param` in the REGISTRY pattern
 * matches exactly one non-empty segment of `path` — the same matcher
 * `demoBoundary.test.ts`'s (now retired) local `entryForDestination` used,
 * lifted here and exported so there is one implementation (TASK-33343000,
 * spec §6.1).
 */
export const registryEntryFor = (path: string): DemoRouteEntry | undefined =>
  DEMO_REGISTRY.find((entry) => {
    const pattern = entry.path.split('/').filter(Boolean)
    const actual = path.split('/').filter(Boolean)
    if (pattern.length !== actual.length) return false
    return pattern.every(
      (segment, i) => segment.startsWith(':') || segment === actual[i]
    )
  })

/** Whether a rail item resolves to a live link or must show as disabled. */
export type NavItemState = 'link' | 'coming-soon'

/**
 * THE ONE RULE the rail derives state from (TASK-33343000, spec §6.1/(a)).
 * Total over every input, including `undefined` (a deck screen with no
 * route) and a path that matches no registry row — both degrade to
 * `'coming-soon'` rather than throwing or defaulting to a link, so an
 * unclassified surface can never be claimed as built.
 */
export const navStateForPath = (path: string | undefined): NavItemState => {
  if (path === undefined) return 'coming-soon'
  const entry = registryEntryFor(path)
  if (entry === undefined) return 'coming-soon'
  if (entry.mode === 'live') return 'link'
  return entry.conceptScope === 'section' ? 'link' : 'coming-soon'
}

export const DEMO_REGISTRY: readonly DemoRouteEntry[] = [
  /* ── Entry points and the unguarded front doors ─────────────────────── */
  {
    path: '/',
    mode: 'live',
    label: 'Role landing',
    triad: 'none',
    pageModule: 'pages/RoleLanding.tsx',
  },
  {
    // TASK-33343000: flipped to live — Home is now the module overview,
    // reading the registry and the role policy only, no fixture content.
    path: '/home',
    mode: 'live',
    label: 'Home',
    triad: 'none',
    pageModule: 'pages/HomePage.tsx',
  },
  {
    path: '/login',
    mode: 'live',
    label: 'Sign in',
    triad: 'none',
    pageModule: 'pages/LoginPage.tsx',
  },
  {
    path: '/oauth/consent',
    mode: 'live',
    label: 'OAuth consent',
    triad: 'none',
    pageModule: 'features/oauth-consent/pages/OAuthConsentPage.tsx',
  },
  {
    path: '/onboarding/01',
    mode: 'live',
    label: 'Register + first organisation',
    triad: 'none',
    pageModule: 'pages/onboarding/OnboardingStep01Page.tsx',
  },
  {
    path: '/accept-invite',
    mode: 'live',
    label: 'Accept invitation',
    triad: 'none',
    pageModule: 'pages/AcceptInvitePage.tsx',
  },
  {
    // TASK-714FBFBD (CR-SSO-INVITE-PX-2026-09-26): the SSO callback landing.
    path: '/auth/callback',
    mode: 'live',
    label: 'Signing in',
    triad: 'none',
    pageModule: 'pages/SsoCallbackPage.tsx',
  },
  {
    path: '/portal',
    mode: 'concept',
    conceptScope: 'section',
    label: 'Client portal',
    triad: 'data',
    pageModule: 'pages/portal/ClientPortalPage.tsx',
    retireWhen: 'client-portal backend (the deck’s fourth module, undecided)',
  },
  {
    path: '/designs',
    mode: 'live',
    label: 'Design exploration',
    triad: 'none',
    pageModule: 'pages/DesignsPage.tsx',
  },
  {
    path: '/architecture',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Architecture map',
    triad: 'none',
    pageModule: 'pages/ArchitectureMapPage.tsx',
    retireWhen:
      'never — it documents the Rev A taxonomy, it is not a product surface',
  },

  /* ── My work ────────────────────────────────────────────────────────── */
  {
    path: '/my/board',
    mode: 'live',
    label: 'My board',
    triad: 'data',
    pageModule: 'pages/my/MyBoardPage.tsx',
  },
  {
    path: '/my/project',
    mode: 'live',
    label: 'My project',
    triad: 'data',
    pageModule: 'pages/my/MyBoardPage.tsx',
  },
  {
    path: '/my/team',
    mode: 'concept',
    conceptScope: 'page',
    label: 'My team',
    triad: 'none',
    pageModule: 'pages/my/ManagerLandingPage.tsx',
    retireWhen: 'capacity + staffing backend (post-W6 wave)',
  },
  {
    path: '/my/client',
    mode: 'live',
    label: 'My client',
    triad: 'data',
    pageModule: 'pages/my/ClientPortfolioPage.tsx',
  },
  {
    path: '/my/timesheet',
    mode: 'live',
    label: 'Timesheet',
    triad: 'data',
    pageModule: 'pages/my/TimesheetPage.tsx',
  },
  {
    path: '/my/approvals',
    mode: 'live',
    label: 'Approvals',
    triad: 'data',
    pageModule: 'pages/my/ApprovalsPage.tsx',
  },

  /* ── Administration ─────────────────────────────────────────────────── */
  {
    path: '/admin/projects',
    mode: 'live',
    label: 'Projects & people (admin)',
    triad: 'data',
    pageModule: 'pages/admin/AdminProjectsPage.tsx',
  },
  {
    path: '/admin/clients',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Clients & modules (super admin)',
    triad: 'none',
    pageModule: 'pages/admin/SuperAdminClientsPage.tsx',
    retireWhen: 'super-admin cross-firm backend',
  },
  {
    path: '/admin/people',
    mode: 'live',
    label: 'People & access',
    triad: 'data',
    pageModule: 'pages/admin/PeopleAdminPage.tsx',
  },
  {
    path: '/admin/settings',
    mode: 'live',
    label: 'Organisation settings',
    triad: 'data',
    pageModule: 'pages/admin/OrgSettingsPage.tsx',
  },
  {
    path: '/admin/audit',
    mode: 'live',
    label: 'Audit log',
    triad: 'data',
    pageModule: 'pages/admin/AuditLogPage.tsx',
  },
  {
    path: '/admin/rates',
    mode: 'live',
    label: 'Rate cards',
    triad: 'data',
    pageModule: 'pages/admin/RateCardsPage.tsx',
  },
  {
    path: '/admin/modules',
    mode: 'live',
    label: 'Module catalogue',
    triad: 'data',
    pageModule: 'pages/admin/ModulesPage.tsx',
  },
  {
    path: '/admin/connectors',
    mode: 'live',
    label: 'Connectors',
    triad: 'data',
    pageModule: 'pages/admin/ConnectorsPage.tsx',
  },
  {
    path: '/admin/connectors/callback',
    mode: 'live',
    label: 'OAuth callback',
    triad: 'none',
    pageModule: 'pages/admin/OAuthCallbackPage.tsx',
  },
  {
    path: '/admin/runtime',
    mode: 'live',
    label: 'Runtime',
    triad: 'data',
    pageModule: 'pages/admin/RuntimePage.tsx',
  },
  {
    path: '/clients/register',
    mode: 'live',
    label: 'Client register',
    triad: 'data',
    pageModule: 'pages/clients/ClientsRegisterPage.tsx',
  },
  {
    path: '/notifications',
    mode: 'live',
    label: 'Notification centre',
    triad: 'data',
    pageModule: 'pages/NotificationsPage.tsx',
  },
  {
    path: '/messages',
    mode: 'live',
    label: 'Messages',
    triad: 'data',
    pageModule: 'pages/MessagesPage.tsx',
  },

  /* ── Resources ──────────────────────────────────────────────────────── */
  {
    path: '/resources',
    mode: 'live',
    label: 'Resources (redirect)',
    triad: 'none',
  },
  {
    path: '/resources/pool',
    mode: 'live',
    label: 'People pool',
    triad: 'data',
    pageModule: 'pages/resources/ResourcePoolPage.tsx',
  },
  {
    path: '/resources/people',
    mode: 'live',
    label: 'People (redirect)',
    triad: 'none',
  },
  {
    path: '/resources/people/:personId',
    mode: 'concept',
    conceptScope: 'section',
    label: 'Person profile',
    triad: 'data',
    pageModule: 'pages/resources/PersonProfilePage.tsx',
    retireWhen:
      'skills / booking backend (the deep profile); the member record is already live',
  },
  {
    path: '/resources/capacity',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Capacity forecast',
    triad: 'none',
    pageModule: 'pages/resources/CapacityForecastPage.tsx',
    retireWhen: 'skills / capacity backend (post-W6 wave)',
  },
  {
    path: '/resources/allocations',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Allocations',
    triad: 'none',
    pageModule: 'pages/resources/AllocationPage.tsx',
    retireWhen: 'skills / capacity backend (post-W6 wave)',
  },
  {
    path: '/resources/skills',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Skills matrix',
    triad: 'none',
    pageModule: 'pages/resources/SkillsMatrixPage.tsx',
    retireWhen: 'skills / capacity backend (post-W6 wave)',
  },
  {
    path: '/resources/utilisation',
    mode: 'concept',
    conceptScope: 'section',
    label: 'Utilisation',
    triad: 'data',
    pageModule: 'pages/resources/UtilisationPage.tsx',
    retireWhen:
      'utilisation + margin backend (the office table); velocity and cycle time are already live',
  },
  {
    path: '/resources/pipeline',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Talent pipeline',
    triad: 'none',
    pageModule: 'pages/resources/TalentPipelinePage.tsx',
    retireWhen: 'Kosovo pipeline backend',
  },
  {
    path: '/resources/training',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Training center',
    triad: 'none',
    pageModule: 'pages/resources/TrainingCenterPage.tsx',
    retireWhen: 'training backend',
  },
  {
    path: '/resources/badges',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Badges',
    triad: 'none',
    pageModule: 'pages/resources/BadgesPage.tsx',
    retireWhen: 'badge-versioning backend',
  },

  /* ── Portfolio ──────────────────────────────────────────────────────── */
  {
    path: '/projects',
    mode: 'live',
    label: 'Portfolio board',
    triad: 'data',
    pageModule: 'pages/projects/PortfolioBoardPage.tsx',
  },
  {
    path: '/projects/health',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Portfolio health',
    triad: 'none',
    pageModule: 'pages/projects/PortfolioHealthPage.tsx',
    retireWhen: 'health-scoring backend (no backend today)',
  },
  {
    path: '/clients',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Clients & AMS',
    triad: 'none',
    pageModule: 'pages/projects/ClientsAmsPage.tsx',
    retireWhen:
      'AMS contract backend (the client register at /clients/register is live)',
  },

  /* ── Library ────────────────────────────────────────────────────────── */
  {
    path: '/library',
    mode: 'live',
    label: 'Library (redirect)',
    triad: 'none',
  },
  {
    path: '/library/templates',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Template library',
    triad: 'none',
    pageModule: 'pages/library/TemplateLibraryPage.tsx',
    retireWhen: 'template backend (post-W6 wave)',
  },
  {
    path: '/library/templates/:templateId',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Template detail',
    triad: 'none',
    pageModule: 'pages/library/TemplateDetailPage.tsx',
    retireWhen: 'template backend (post-W6 wave)',
  },
  {
    path: '/library/requirements',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Requirements library',
    triad: 'none',
    pageModule: 'pages/library/RequirementsLibraryPage.tsx',
    retireWhen: 'W3 requirements register backend',
  },
  {
    path: '/library/agents',
    mode: 'live',
    label: 'Agent library',
    triad: 'data',
    pageModule: 'pages/library/AgentLibraryPage.tsx',
  },
  {
    path: '/library/agents/:agentId',
    mode: 'live',
    label: 'Agent configuration',
    triad: 'data',
    pageModule: 'pages/library/AgentConfigPage.tsx',
  },
  {
    path: '/library/agents/:agentId/guardrails',
    mode: 'live',
    label: 'Agent guardrails',
    triad: 'data',
    pageModule: 'pages/library/AgentGuardrailsPage.tsx',
  },
  {
    path: '/library/blueprints',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Blueprint configuration',
    triad: 'none',
    pageModule: 'pages/library/BlueprintConfigPage.tsx',
    retireWhen: 'W3 blueprint backend',
  },
  {
    path: '/library/playbooks',
    mode: 'concept',
    conceptScope: 'section',
    label: 'Playbook configuration',
    triad: 'data',
    pageModule: 'pages/library/PlaybookConfigPage.tsx',
    retireWhen:
      'playbook authoring backend (the phase table); playbooks + tenant policy are already live',
  },

  /* ── One engagement ─────────────────────────────────────────────────── */
  {
    path: '/project',
    mode: 'live',
    label: 'Project (resolves the first engagement)',
    triad: 'none',
    pageModule: 'components/px/ProjectRedirect.tsx',
  },
  {
    path: '/project/:projectId',
    mode: 'concept',
    conceptScope: 'section',
    label: 'Project overview',
    triad: 'data',
    pageModule: 'pages/project/ProjectOverviewPage.tsx',
    retireWhen:
      'agent-run list endpoint — the overnight-runs panel; everything else on the overview is live',
  },
  {
    path: '/project/:projectId/interviews',
    mode: 'live',
    label: 'Interviews',
    triad: 'data',
    pageModule: 'pages/project/InterviewsPage.tsx',
  },
  {
    path: '/project/:projectId/requirements',
    mode: 'live',
    label: 'Requirements register',
    triad: 'data',
    pageModule: 'pages/project/RequirementsRegisterPage.tsx',
  },
  {
    path: '/project/:projectId/blueprint',
    mode: 'concept',
    conceptScope: 'section',
    label: 'Blueprint',
    triad: 'data',
    pageModule: 'pages/project/BlueprintPage.tsx',
    retireWhen: 'Blueprint structure backend is available',
  },
  {
    path: '/project/:projectId/build',
    mode: 'live',
    label: 'Build board',
    triad: 'data',
    pageModule: 'pages/project/BuildBoardPage.tsx',
  },
  {
    path: '/project/:projectId/migration',
    mode: 'concept',
    conceptScope: 'section',
    label: 'Migration',
    triad: 'data',
    pageModule: 'pages/project/MigrationPage.tsx',
    retireWhen:
      'column-level field-mapping backend (mapping rows, proposed fields, ambiguous-column assignment) — no model exists; plans, runs, reconciliation, go/no-go, rollback and trail are live',
  },
  {
    path: '/project/:projectId/test',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Test & UAT',
    triad: 'none',
    pageModule: 'pages/project/TestUatPage.tsx',
    retireWhen: 'W5 test / UAT',
  },
  {
    path: '/project/:projectId/go-live',
    mode: 'concept',
    conceptScope: 'page',
    label: 'Go-live plan',
    triad: 'none',
    pageModule: 'pages/project/GoLivePage.tsx',
    retireWhen: 'go-live runbook backend (post-W6 wave)',
  },
  {
    path: '/project/:projectId/ams',
    mode: 'concept',
    conceptScope: 'page',
    label: 'AMS & upgrades',
    triad: 'none',
    pageModule: 'pages/project/MaintainPage.tsx',
    retireWhen: 'AMS contract backend (post-W6 wave)',
  },
  {
    path: '/project/:projectId/budget',
    mode: 'live',
    label: 'Project budget',
    triad: 'data',
    pageModule: 'pages/project/ProjectBudgetPage.tsx',
  },
  {
    path: '/project/:projectId/agent-spend',
    mode: 'live',
    label: 'Agent spend',
    triad: 'data',
    pageModule: 'pages/project/AgentSpendPage.tsx',
  },
] as const

/**
 * SHARED components whose CONTENT is demo material. They are not pages, so no
 * registry row can classify them, and they sit under `components/px/` because
 * that is where every shared component lives — but they render fixture rows,
 * so they may import `@/demo/*`.
 *
 * The boundary suite enforces the other half of that licence: each is imported
 * ONLY by concept pages or by another entry in this list, so a LIVE surface
 * pulling one in fails naming the file.
 */
export const DEMO_CONTENT_COMPONENTS: readonly {
  module: string
  label: string
  retireWhen: string
}[] = [
  {
    module: 'components/px/KanbanBoard.tsx',
    label: 'Work-package kanban',
    retireWhen: 'W4 build board',
  },
  {
    module: 'components/px/PersonChip.tsx',
    label: 'Person chip (seed roster)',
    retireWhen: 'skills / capacity backend (post-W6 wave)',
  },
  {
    module: 'components/px/CustomerFlowSection.tsx',
    label: 'Customer-flow walkthrough',
    retireWhen:
      'never — it documents the onboarding flow, it is not a product surface',
  },
]

/* ── Derived views (the README and the audit suites read these) ─────────── */

export const liveEntries = (): readonly DemoRouteEntry[] =>
  DEMO_REGISTRY.filter((entry) => entry.mode === 'live')

export const conceptEntries = (): readonly DemoRouteEntry[] =>
  DEMO_REGISTRY.filter((entry) => entry.mode === 'concept')

/** Concept surfaces that are demo end to end — the network-quiet census. */
export const conceptPageEntries = (): readonly DemoRouteEntry[] =>
  DEMO_REGISTRY.filter(
    (entry) => entry.mode === 'concept' && entry.conceptScope === 'page'
  )

/** Hybrids: a live spine with a badged concept section. */
export const conceptSectionEntries = (): readonly DemoRouteEntry[] =>
  DEMO_REGISTRY.filter(
    (entry) => entry.mode === 'concept' && entry.conceptScope === 'section'
  )

/** Every route whose view must implement the loading/error/empty triad. */
export const triadEntries = (): readonly DemoRouteEntry[] =>
  DEMO_REGISTRY.filter((entry) => entry.triad === 'data')

/** Page modules a concept route renders — the boundary allow list's data half. */
export const conceptPageModules = (): readonly string[] => [
  ...new Set(
    conceptEntries()
      .map((entry) => entry.pageModule)
      .filter((module): module is string => module !== undefined)
  ),
]
